/**
 * Explicit failure taxonomy for the durable photo pipeline.
 *
 * Before this existed, ANY `NotFoundError` raised while reading local photo
 * bytes was attributed to "the camera temp file was purged" and treated as
 * terminal. That assumption was proven wrong in production 1.56: the error was
 * raised by IndexedDB when reading a stored Blob after a WebView restart, and
 * perfectly recoverable photos were written off as support exceptions.
 *
 * Each failure now carries a category, and only a category that has been
 * independently established may be terminal.
 */
export type PhotoFailureKind =
  /** The original camera source (temp file / stream) is genuinely gone. */
  | "camera_source_unavailable"
  /** Writing the durable bytes into the local store failed. */
  | "durable_storage_write_failed"
  /**
   * The local store refused the write because the browser/device storage quota
   * is exhausted (QuotaExceededError and equivalents).
   *
   * This is a *write* failure, never evidence that a photo is lost: existing
   * durable bytes are untouched, and the existing capture fallback (verified
   * cloud copy when online) still applies. It is deliberately NOT part of
   * {@link isDurableReadFailure}, so it can never contribute to the terminal
   * durable-read policy.
   */
  | "durable_storage_quota_exceeded"
  /** Reading the durable bytes back from the local store failed. */
  | "durable_storage_read_failed"
  /** Durable bytes were returned but are empty/not a decodable image payload. */
  | "durable_storage_corrupt"
  /** The upload request itself failed for network reasons. */
  | "network_upload_failed"
  /** Storage rejected or errored on the upload. */
  | "storage_upload_failed"
  /** Upload reported success but the object could not be verified. */
  | "storage_verification_failed"
  /**
   * The payload length did not match the length recorded for the durable
   * bytes (1.60.51). Recoverable: the durable bytes are retained and the photo
   * stays in the existing retry path. Never terminal, never archival evidence.
   */
  | "integrity_length_mismatch"
  /**
   * The payload SHA-256 did not match the hash recorded for the durable bytes
   * (1.60.51). Recoverable in exactly the same way as a length mismatch.
   */
  | "integrity_hash_mismatch"
  /**
   * Storage refused the upload for AUTHORISATION reasons (HTTP 403 / row-level
   * security). Production case (1.63): `vehicle_appraisals.company_id` was
   * NULL, so the appraisal-photo INSERT policy rejected every upload.
   *
   * This is a permanent, ACTIONABLE data problem, not a transient network
   * fault: retrying at network cadence can never succeed. The durable bytes
   * are always retained and the record stays recoverable, so once the
   * underlying data is corrected the existing retry path uploads it normally.
   */
  | "storage_authorization_denied";

export class PhotoPipelineError extends Error {
  readonly kind: PhotoFailureKind;
  /** True only when the durable read timed out rather than errored. */
  readonly timedOut: boolean;
  readonly cause?: unknown;

  constructor(kind: PhotoFailureKind, message: string, opts: { timedOut?: boolean; cause?: unknown } = {}) {
    super(message);
    this.name = "PhotoPipelineError";
    this.kind = kind;
    this.timedOut = opts.timedOut === true;
    this.cause = opts.cause;
  }
}

/** Failures that mean "the bytes could not be read right now" — always retryable. */
export function isDurableReadFailure(error: unknown): boolean {
  const kind = (error as PhotoPipelineError)?.kind;
  return kind === "durable_storage_read_failed" || kind === "durable_storage_corrupt";
}

/**
 * Storage authorisation/RLS rejection detection (1.63).
 *
 * Deliberately narrow: only HTTP 403 and explicit row-level-security /
 * "Unauthorized" rejections. 401 is NOT included — the existing pipeline
 * treats it as a transient auth blip and refreshes the session. No other 4xx
 * status is reclassified.
 */
export function isStorageAuthorizationDenied(error: unknown): boolean {
  if (!error) return false;
  const value = error as { statusCode?: unknown; status?: unknown; message?: unknown; error?: unknown };
  const raw = value.statusCode ?? value.status;
  const status = typeof raw === "string" ? Number.parseInt(raw, 10) : typeof raw === "number" ? raw : null;
  // 1.65 (J-1074) — a SUCCESSFUL Storage response can never be an
  // authorisation denial. Previously any object-shaped response whose fields
  // stringified oddly could reach the text test below; a 2xx short-circuits.
  if (typeof status === "number" && status >= 200 && status < 300) return false;
  // Only genuine textual evidence counts. A non-string `error` field (Storage
  // returns an object in some SDK paths) stringifies to "[object Object]" and
  // must never be treated as evidence of anything.
  const message = typeof value.message === "string" ? value.message : "";
  const errorText = typeof value.error === "string" ? value.error : "";
  const text = `${message} ${errorText}`;
  if (/row-level security|row level security|violates row-level/i.test(text)) return true;
  if (status === 403) return true;
  return false;
}


/**
 * Transient Storage failures that must keep retrying at normal cadence:
 * 5xx, database timeouts, network faults and request timeouts.
 */
export function isTransientStorageFailure(error: unknown): boolean {
  if (!error) return false;
  if (isStorageAuthorizationDenied(error)) return false;
  const value = error as { statusCode?: unknown; status?: unknown; message?: string; error?: string };
  const raw = value.statusCode ?? value.status;
  const status = typeof raw === "string" ? Number.parseInt(raw, 10) : typeof raw === "number" ? raw : null;
  if (typeof status === "number" && status >= 500) return true;
  const text = `${value.message ?? ""} ${value.error ?? ""}`;
  return /timeout|timed out|databasetimeout|network|fetch|abort|temporarily unavailable|unavailable/i.test(text);
}

/**
 * Browser/device storage-quota failure detection.
 *
 * Covers the standard `QuotaExceededError`, the legacy Firefox
 * `NS_ERROR_DOM_QUOTA_REACHED` (code 1014) and Safari's older numeric code 22,
 * plus IndexedDB implementations that only report it in the message text.
 *
 * A quota failure is explicitly NOT photo loss: it means the bytes could not be
 * written *here, now*. Existing durable records are never deleted or archived
 * because of it.
 */
export function isQuotaExceededError(error: unknown): boolean {
  if (!error) return false;
  const value = error as { name?: string; code?: number; message?: string };
  if (value.name === "QuotaExceededError" || value.name === "NS_ERROR_DOM_QUOTA_REACHED") return true;
  if (value.code === 22 || value.code === 1014) return true;
  return /quota|storage is full|exceeded the quota/i.test(value.message || "");
}
